import { useEffect, useRef } from 'react'

// Shared observer for better performance — one observer for all elements
const observerMap = new WeakMap<Element, () => void>()
let sharedObserver: IntersectionObserver | null = null

function getSharedObserver() {
  if (!sharedObserver) {
    sharedObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const callback = observerMap.get(entry.target)
            if (callback) {
              callback()
              observerMap.delete(entry.target)
              sharedObserver?.unobserve(entry.target)
            }
          }
        })
      },
      { threshold: 0.1, rootMargin: '0px 0px -50px 0px' },
    )
  }
  return sharedObserver
}

export function useScrollReveal() {
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const element = ref.current
    if (!element) return

    // Initial state using class-based approach for better performance
    element.style.opacity = '0'
    element.style.transform = 'translateY(30px)'
    element.style.transition =
      'opacity 0.8s cubic-bezier(0.25, 0.46, 0.45, 0.94), transform 0.8s cubic-bezier(0.25, 0.46, 0.45, 0.94)'

    const observer = getSharedObserver()

    observerMap.set(element, () => {
      element.style.opacity = '1'
      element.style.transform = 'translateY(0)'
    })

    observer.observe(element)

    return () => {
      if (element) {
        observer.unobserve(element)
        observerMap.delete(element)
      }
    }
  }, [])

  return ref
}
